/**
 * Dunning status for the current viewer — drives the payment-failed warning
 * banner + modal (DunningWarning).
 *
 * A subscription in Stripe's retry window sits in status 'past_due' with
 * `past_due_since` stamped by provisioning (hermes mig 480) at the FIRST
 * failure and held there across retries. Access continues through the window
 * (mig 475 grace); when Stripe exhausts its retries it cancels the
 * subscription and the deletion webhook revokes access. The member-facing
 * deadline shown here is therefore `past_due_since + DUNNING_GRACE_DAYS`.
 *
 * Sources checked: the viewer's own K&C membership seat AND the current org's
 * Alfie subscription rows — either in dunning shows the warning (a K&C sub in
 * dunning flips both in the same webhook, so this usually collapses to one).
 * Hardship-held rows (hold_state_id set) are a deliberate admin arrangement,
 * not dunning — suppressed.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase, getSessionUser } from '@/lib/supabase';
import { useCurrentOrganization } from '@/hooks/use-bss-organizations';

/**
 * Days from first failed charge until access is revoked. Mirrors the Stripe
 * dashboard dunning config (Settings → Billing → Automatic collection:
 * "retry up to 8 times within 2 weeks", then "cancel the subscription") —
 * Stripe does not expose that config via API, so if the dashboard window ever
 * changes, this constant must change with it.
 */
export const DUNNING_GRACE_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface DunningSourceRow {
  status: string;
  past_due_since: string | null;
  hold_state_id: string | null;
}

export interface DunningStatus {
  inDunning: boolean;
  /** Earliest first-failure moment across in-dunning rows; null pre-480 rows. */
  since: Date | null;
  /** since + DUNNING_GRACE_DAYS; null when since is unknown. */
  deadline: Date | null;
  /** Whole days until the deadline, floored at 0; null when since is unknown. */
  daysLeft: number | null;
}

const NOT_IN_DUNNING: DunningStatus = {
  inDunning: false,
  since: null,
  deadline: null,
  daysLeft: null,
};

/**
 * Pure resolution: in dunning iff any non-held row is past_due. The deadline
 * derives from the EARLIEST stamped clock (several rows usually share one
 * Stripe subscription and stamp together; earliest is the honest promise).
 * A past_due row with no stamp (written before mig 480 deployed) still warns,
 * just without a date.
 */
export function resolveDunning(rows: DunningSourceRow[], now: Date): DunningStatus {
  const dunningRows = rows.filter(
    (r) => r.status === 'past_due' && !r.hold_state_id,
  );
  if (dunningRows.length === 0) return NOT_IN_DUNNING;

  const stamps = dunningRows
    .map((r) => (r.past_due_since ? new Date(r.past_due_since) : null))
    .filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  const since = stamps[0] ?? null;
  if (!since) return { inDunning: true, since: null, deadline: null, daysLeft: null };

  const deadline = new Date(since.getTime() + DUNNING_GRACE_DAYS * MS_PER_DAY);
  const daysLeft = Math.max(
    0,
    Math.ceil((deadline.getTime() - now.getTime()) / MS_PER_DAY),
  );
  return { inDunning: true, since, deadline, daysLeft };
}

export function dunningStatusKey(orgId: string | null) {
  return ['dunning-status', orgId] as const;
}

export function useDunningStatus() {
  const { data: orgData } = useCurrentOrganization();
  const orgId =
    (orgData as { organization?: { id?: string } } | null)?.organization?.id ?? null;

  return useQuery<DunningStatus>({
    queryKey: dunningStatusKey(orgId),
    queryFn: async () => {
      const userId = (await getSessionUser())?.id ?? null;
      if (!userId) return NOT_IN_DUNNING;

      const [membershipRes, orgSubsRes] = await Promise.all([
        supabase
          .from('user_community_memberships')
          .select('status, past_due_since, hold_state_id')
          .eq('user_id', userId),
        orgId
          ? supabase
            .from('organization_alfie_subscriptions')
            .select('status, past_due_since, hold_state_id')
            .eq('organization_id', orgId)
          : Promise.resolve({ data: [], error: null }),
      ]);
      // Fail quiet to "no warning": this surface is advisory — a read blip must
      // not throw a scary banner (or an error boundary) over the whole app.
      if (membershipRes.error || orgSubsRes.error) return NOT_IN_DUNNING;

      const rows = [
        ...((membershipRes.data ?? []) as DunningSourceRow[]),
        ...((orgSubsRes.data ?? []) as DunningSourceRow[]),
      ];
      return resolveDunning(rows, new Date());
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
